import React, { useCallback } from "react";
import {
    DA_DynamicOverlayWrapper,
    useDynamicOverlay,
} from "@danishagro/shared/src/hooks/dynamicOverlay/useDynamicOverlay.hook";
import { DA_BasicLink } from "@danishagro/shared/src/components/atoms/BasicLink/BasicLink.component";
import { DA_Image } from "@danishagro/shared/src/components/atoms/Image/Image.component";
import {
    DA_Button,
    DA_ButtonColor,
} from "@danishagro/shared/src/components/atoms/Button/Button.component";
import { useTranslations } from "@danishagro/shared/src/contexts/translations/translations.context";
import { DA_QuantityPicker } from "@molecules/QuantityPicker/QuantityPicker.component";
import { useAppData } from "@danishagro/shared/src/contexts/appData.context";
import { B2bImageSrc } from "@danishagro/shared/src/helpers/imageSrc.helper";
import { ImageConfig } from "@danishagro/shared/src/content/imageConfigs/imageConfig.enum";
import { useMaxValue } from "@hooks/useMaxValue.hook";
import { formatNumber } from "@danishagro/shared/src/helpers/formatNumber.helper";
import { DA_CartItemDrawerProps } from "./CartItemDrawer.interface";
// eslint-disable-next-line css-modules/no-unused-class
import S from "./CartItemDrawer.module.scss";

export const DA_CartItemDrawer = ({ ...props }: DA_CartItemDrawerProps) => {
    const { getDictionaryString } = useTranslations();
    const { siteVariant } = useAppData();
    const { closeOverlay } = useDynamicOverlay();
    const highMax = useMaxValue(siteVariant);

    const formatPrice = useCallback(
        (price: number) => formatNumber(price, "da-DK", { decimals: 2 }),
        []
    );

    return (
        <DA_DynamicOverlayWrapper
            content={
                <div className={S.drawerProductItem}>
                    <div className={S.mainInfo}>
                        {/** Image */}
                        <div className={S.imageWrapper}>
                            <DA_BasicLink href={props.url} className={S.imageLink}>
                                <DA_Image
                                    src={B2bImageSrc(props.imageUrl, ImageConfig.PlpImage)}
                                    alt={props.sizedImageMobile.alt}
                                    width={props.sizedImageMobile.width}
                                    height={props.sizedImageMobile.height}
                                    className={S.image}
                                />
                            </DA_BasicLink>
                        </div>

                        {/** Name */}
                        <div className={S.numberAndName}>
                            <div className={S.number}>{props.productNumber}</div>

                            <div className={S.name}>
                                <DA_BasicLink href={props.url} className={S.nameLink}>
                                    {props.productName}
                                </DA_BasicLink>
                            </div>
                        </div>
                    </div>
                    <div className={S.mobileActionWrapper}>
                        {/* Amount picker */}
                        <div className={S.mobileActionWrapperItem}>
                            <div className={S.actionAmountWrapper}>
                                <div className={S.actionAmountWrapperTitle}>
                                    {getDictionaryString("amount")}
                                </div>

                                <div className={S.actionAmountWrapperInput}>
                                    {!props.readOnly ? (
                                        <DA_QuantityPicker
                                            onChange={props.updateQuantity}
                                            value={props.quantity}
                                            disabled={props.dimmed}
                                            higherMax={highMax}
                                        />
                                    ) : (
                                        <div>
                                            {props.quantity} {props.unit}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/** Price */}
                        <div className={S.mobileActionWrapperItem}>
                            <div className={S.total}>
                                <span className={S.totalLabel}>
                                    {getDictionaryString("general.price")}
                                </span>

                                <span className={S.totalPrice}>
                                    {formatPrice(props.linePrice?.totalPrice)}
                                </span>
                            </div>
                        </div>

                        <div />
                    </div>
                </div>
            }
            footer={
                <div className={S.footerDrawerBtns}>
                    {props.isReceiptPage ? (
                        <DA_Button
                            title={getDictionaryString("general.close")}
                            isGhost={true}
                            onClick={closeOverlay}
                            className={S.button}
                        />
                    ) : (
                        <>
                            <DA_Button
                                title={getDictionaryString("remove")}
                                isGhost={true}
                                color={DA_ButtonColor.Alert}
                                onClick={props.confirmRemove}
                                className={S.button}
                            />
                            <DA_Button
                                title={getDictionaryString("general.close")}
                                onClick={props.closeOverlay}
                                className={S.button}
                            />
                        </>
                    )}
                </div>
            }
        />
    );
};
